import React from "react";
import { css } from "@emotion/react";
import { Trans, useTranslation } from "react-i18next";
import { useAppSelector } from "main/javascripts/store";
import { similarCruisesSelector } from "../cruiseSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import Link from "next/link";
import {
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { numberToCurrency } from "main/javascripts/utils/StringUtil";

export const SimilarCruiseList: React.FC = (): React.ReactElement => {
  const { t } = useTranslation(["component", "unit"]);

  const similarCruises = useAppSelector(similarCruisesSelector);

  return (
    <div css={blockStyle}>
      <h2 css={titleStyle}>{t("cruise.similarCruises")}</h2>
      <div css={cruiseListStyle}>
        {similarCruises.map((cruise) => {
          const priceValue = numberToCurrency(cruise.minPriceCents, "JPY");
          return (
            <Link href={`/cruises/${cruise.uniqueId}`} key={cruise.id} passHref>
              <a css={cruiseListLinkStyle}>
                <div css={cruiseListItemStyle}>
                  <div>
                    <div css={cruiseTitleStyle}>{cruise.title}</div>
                    <div css={priceStyle}>
                      <Trans
                        i18nKey="unit:priceStartsAt"
                        values={{ value: priceValue }}
                      >
                        from
                        <span css={priceValueStyle}>{priceValue}</span> p.p.
                      </Trans>
                    </div>
                  </div>
                </div>
              </a>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const blockStyle = css`
  width: 100%;
`;
const titleStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline2};
  padding: ${space.atom2x} 0;
`;
const cruiseListStyle = css`
  padding: ${space.atom2_5x} 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(276px, 1fr));
  gap: ${space.atom2x};
`;
const cruiseListLinkStyle = css`
  text-decoration: none;
  color: inherit;
`;
const cruiseListItemStyle = css`
  border: 1px solid ${borderColor.secondaryLightColor};
  padding: ${space.atom2x};
  display: grid;
  grid-template-columns: 1fr 2rem;
  gap: ${space.atom0_5x};
  border-radius: ${borderRadius.large};
  transition: border 0.2s ease;
  position: relative;
  &:hover {
    border-color: ${borderColor.primaryDarkColor};
  }
  &::after {
    content: "";
    width: 1.5rem;
    height: 1.5rem;
    border: 1px solid ${borderColor.primaryDarkColor};
    border-radius: 50%;
    position: absolute;
    right: 0.875rem;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border 0.2s ease;
  }
  &::before {
    content: "";
    width: 0.4375rem;
    height: 0.4375rem;
    border-top: 1px solid ${borderColor.primaryDarkColor};
    border-right: 1px solid ${borderColor.primaryDarkColor};
    position: absolute;
    right: 1.5rem;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    z-index: 1;
    transition: border 0.2s ease;
  }
  &:hover::after {
    border-color: ${textColor.primaryDarkColor};
  }
  &:hover::before {
    border-color: ${textColor.primaryDarkColor};
  }
`;
const cruiseTitleStyle = css`
  font-size: ${fontSize.mediumHeading};
  line-height: ${lineHeight.mediumHeadline2};
  font-weight: ${fontWeight.medium};
`;
const priceStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  color: ${textColor.secondaryDarkColor};
`;
const priceValueStyle = css`
  font-size: ${fontSize.mediumHeading};
  line-height: ${lineHeight.mediumHeadline2};
  font-variant-numeric: tabular-nums;
  color: ${textColor.primaryDarkColor};
`;
